import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, usePropertyField } from '@vibesites/runtime/react';
import { EditableToken, RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal, useStaggerReveal } from '@vibesites/theme-rise/hooks/useScrollReveal';
import { useRevealStyles } from '@vibesites/theme-rise/hooks/useRevealStyles';

// ============================================================================
// Benefit card data
// ============================================================================

interface Benefit {
  icon: React.ReactNode;
  tokenKey: string;
  defaultTitle: string;
  defaultDescription: string;
}

const BENEFITS: Benefit[] = [
  {
    icon: <PreApprovedIcon />,
    tokenKey: 'pre_approved',
    defaultTitle: 'Pre-Approved',
    defaultDescription:
      'Run your application now, and get pre-approved for move-in. Get an approved credit and non-refundable app fee.',
  },
  {
    icon: <FirstChoiceIcon />,
    tokenKey: 'first_choice',
    defaultTitle: 'First Choice',
    defaultDescription:
      'As apartments become available for move-in, you get first choice of your new home before the apartments are offered to the market.',
  },
  {
    icon: <PriorityIcon />,
    tokenKey: 'priority',
    defaultTitle: 'Priority',
    defaultDescription:
      'Receive first choice and flexibility of the time frame that you want on move-in day.',
  },
  {
    icon: <CommitmentFreeIcon />,
    tokenKey: 'commitment_free',
    defaultTitle: 'Commitment Free',
    defaultDescription:
      'Your deposit for the Future Residents Club is fully refundable until we have available apartment homes to view and you make your new home at one of our choice communities.',
  },
  {
    icon: <GetToKnowUsIcon />,
    tokenKey: 'get_to_know_us',
    defaultTitle: 'Get to Know Us',
    defaultDescription:
      'You will be automatically signed up for any community correspondence and will be invited to our events. A great way to get to know your neighbors before moving in.',
  },
  {
    icon: <JoiningIsEasyIcon />,
    tokenKey: 'joining_easy',
    defaultTitle: 'Joining Is Easy',
    defaultDescription:
      'Simply fill out an online application by selecting your desired floor plan, select a start date, pay a holding deposit, and then hold your spot in line. Easy!',
  },
];

// ============================================================================
// SVG Icons — clean, minimal line icons matching the theme
// ============================================================================

function PreApprovedIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="frc-icon">
      <rect x="6" y="10" width="36" height="28" rx="3" />
      <path d="M6 18h36" />
      <path d="M19 30l3 3 7-7" />
    </svg>
  );
}

function FirstChoiceIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="frc-icon">
      <path d="M24 4l5.09 10.32L40 15.78l-8 7.8 1.89 11.02L24 29.5l-9.89 5.1L16 23.58l-8-7.8 10.91-1.46z" />
    </svg>
  );
}

function PriorityIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="frc-icon">
      <path d="M8 44V14l16-10 16 10v30" />
      <rect x="18" y="28" width="12" height="16" />
      <rect x="21" y="18" width="6" height="6" />
    </svg>
  );
}

function CommitmentFreeIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="frc-icon">
      <path d="M20 6h8v6h-8z" />
      <path d="M28 8h6a2 2 0 012 2v30a2 2 0 01-2 2H14a2 2 0 01-2-2V10a2 2 0 012-2h6" />
      <path d="M18 24l4 4 8-8" />
    </svg>
  );
}

function GetToKnowUsIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="frc-icon">
      <circle cx="24" cy="16" r="8" />
      <path d="M8 44c0-8.84 7.16-16 16-16s16 7.16 16 16" />
    </svg>
  );
}

function JoiningIsEasyIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="frc-icon">
      <rect x="6" y="6" width="36" height="36" rx="4" />
      <path d="M16 24h16" />
      <path d="M24 16v16" />
    </svg>
  );
}

// ============================================================================
// Benefit Card
// ============================================================================

function BenefitCard({ icon, tokenKey, defaultTitle, defaultDescription }: Benefit) {
  const title = useContentToken(`futureresidentsclub.benefit_${tokenKey}_title`) || defaultTitle;
  const description = useContentToken(`futureresidentsclub.benefit_${tokenKey}_desc`) || defaultDescription;
  return (
    <div data-reveal className="frc-benefit-card">
      <div className="frc-benefit-icon">{icon}</div>
      <EditableToken
        path={`futureresidentsclub.benefit_${tokenKey}_title`}
        as="h3"
        className="font-heading text-base md:text-lg font-medium text-[var(--vibe-color-text)] mb-2 tracking-wide"
      >
        {title}
      </EditableToken>
      <EditableToken
        path={`futureresidentsclub.benefit_${tokenKey}_desc`}
        as="p"
        className="font-body text-sm leading-relaxed text-[var(--vibe-color-text-muted)]"
      >
        {description}
      </EditableToken>
    </div>
  );
}

// ============================================================================
// Slot group definitions
// ============================================================================

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url:     { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label:   { label: 'Button Text', type: 'text', defaultValue: 'View Floor Plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const howImageDef: SlotGroupDefinition = {
  label: 'How It Works Image',
  fields: {
    image_url: { label: 'Image', type: 'image', defaultValue: '' },
    image_alt: { label: 'Image Alt Text', type: 'text', defaultValue: 'Apartment interior' },
  },
};

// ============================================================================
// Main Component
// ============================================================================

export function FutureResidentsClub() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 80 });
  const howItWorksRef = useScrollReveal<HTMLElement>();

  const featuredImage = usePropertyField('property_featured_image') as string | null;

  const communityHeading = useContentToken('futureresidentsclub.community_heading') || 'Join Our Community';
  const howHeading = useContentToken('futureresidentsclub.how_heading') || 'How It Works';

  const cta = useSlotGroup('futureresidentsclub.cta_link', ctaLinkDef);
  const howImage = useSlotGroup('futureresidentsclub.how_image', howImageDef);

  return (
    <>
      {/* Join Our Community — Benefits Grid */}
      <section ref={sectionRef} className="reveal-fade-up py-14 md:py-20 bg-[var(--vibe-color-background)]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <EditableToken
              path="futureresidentsclub.community_heading"
              as="h2"
              className="font-heading text-2xl md:text-3xl font-normal text-[var(--vibe-color-text)] mb-4 uppercase tracking-wider"
            >
              {communityHeading}
            </EditableToken>
            <RichTextContent
              path="futureresidentsclub.community_intro"
              as="p"
              className="font-body text-base md:text-lg leading-relaxed text-[var(--vibe-color-text-muted)] max-w-3xl mx-auto"
            />
          </div>

          <div ref={gridRef} className="frc-benefits-grid">
            {BENEFITS.map((benefit) => (
              <BenefitCard key={benefit.tokenKey} {...benefit} />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section ref={howItWorksRef} className="reveal-fade-up py-14 md:py-20 bg-[var(--vibe-color-primary)]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="frc-how-layout">
            <div className="frc-how-content">
              <EditableToken
                path="futureresidentsclub.how_heading"
                as="h2"
                className="font-heading text-2xl md:text-3xl font-normal text-[var(--vibe-color-text-on-primary)] mb-6 uppercase tracking-wider"
              >
                {howHeading}
              </EditableToken>

              <RichTextContent
                path="futureresidentsclub.how_description"
                as="div"
                className="font-body text-[0.95rem] leading-relaxed text-[var(--vibe-color-text-on-primary)] [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_li]:mb-2 [&_strong]:font-semibold"
              />

              <div className="mt-8">
                <EditableSlotGroup groupPath="futureresidentsclub.cta_link" definition={ctaLinkDef}>
                  <a
                    href={cta.url || '/floor-plans'}
                    target={cta._target || '_self'}
                    className="frc-cta"
                  >
                    {cta.label || 'View Floor Plans'}
                  </a>
                </EditableSlotGroup>
              </div>
            </div>

            <div className="frc-how-image">
              <EditableSlotGroup groupPath="futureresidentsclub.how_image" definition={howImageDef}>
                <VibesitesImage
                  url={howImage.image_url || featuredImage || undefined}
                  alt={howImage.image_alt || 'Apartment interior'}
                  className="w-full h-full object-cover rounded-sm"
                />
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .frc-benefits-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
        }
        @media (min-width: 640px) {
          .frc-benefits-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (min-width: 1024px) {
          .frc-benefits-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        .frc-benefit-card {
          text-align: center;
          padding: 1.5rem 1rem;
        }

        .frc-benefit-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
          color: #000;
        }

        .frc-icon {
          width: 48px;
          height: 48px;
        }

        .frc-how-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
          align-items: start;
        }
        @media (min-width: 768px) {
          .frc-how-layout {
            grid-template-columns: 1fr 1fr;
            gap: 3rem;
            align-items: stretch;
          }
        }

        .frc-how-content {
          order: 2;
        }
        @media (min-width: 768px) {
          .frc-how-content {
            order: 1;
          }
        }

        .frc-how-image {
          order: 1;
          overflow: hidden;
          border-radius: 2px;
        }
        @media (min-width: 768px) {
          .frc-how-image {
            order: 2;
            min-height: 400px;
            display: flex;
            flex-direction: column;
          }
          .frc-how-image > * {
            flex: 1;
            min-height: 0;
          }
        }

        .frc-cta {
          display: inline-block;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--vibe-color-primary);
          background: var(--vibe-color-text-on-primary);
          padding: 0.75rem 2rem;
          border-radius: 2px;
          text-decoration: none;
          transition: opacity 0.2s;
        }
        .frc-cta:hover {
          opacity: 0.85;
        }
      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'FutureResidentsClub',
  displayName: 'Future Residents Club',
  tokens: {
    'futureresidentsclub.community_heading': {
      label: 'Community Section Heading',
      type: 'text',
      defaultValue: 'Join Our Community',
    },
    'futureresidentsclub.community_intro': {
      label: 'Community Intro Text',
      type: 'richtext',
      defaultValue:
        'When moving, you have a vast array of choices to make. Thankfully, choosing your new home is easier with the Future Residents Club. Start creating your sense of community before ever moving into your new home. Here\'s what you get for being a member:',
    },
    'futureresidentsclub.how_heading': {
      label: 'How It Works Heading',
      type: 'text',
      defaultValue: 'How It Works',
    },
    'futureresidentsclub.how_description': {
      label: 'How It Works Description',
      type: 'richtext',
      defaultValue: '<p>When an apartment becomes available, the community will contact our Club members in order of application submission. You will have 72 hours from the time we contact you to come by and tour the actual apartment.</p><p>Once you\'ve seen it, you have 24 hours to:</p><ul><li><strong>Select the unit</strong> (your Future Residents Club deposit is then applied to your apartment deposit);</li><li><strong>Cancel altogether</strong> (your Future Residents Club deposit is refunded);</li><li><strong>Stay on Future Residents Club list</strong> to select another apartment (however, you will be moved to the bottom of the list).</li></ul>',
    },
  },
  slotGroups: {
    'futureresidentsclub.cta_link': ctaLinkDef,
    'futureresidentsclub.how_image': howImageDef,
  },
};

(FutureResidentsClub as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FutureResidentsClub;
